import React, { forwardRef, useEffect, useRef, useState } from "react";
import axios from "axios";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global, globalMenu } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import SnButton from "@shared/components/SnButton";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

// SOP 파일은 레시피등록(SN_PRD_BAS_REG_001_CHEM)에서 업로드되므로 조회 시 해당 메뉴코드 사용
const RECIPE_CD_MENU = "SN_PRD_BAS_REG_001_CHEM";

// 미리보기 지원 가능 형식
const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "bmp", "webp"];

const SN_PRD_RUN_REG_001_CHEM_POP_004 = forwardRef(
  ({ selectedData }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const gridRef = useRef<any>(null);
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource, setDataSource] = useState<any[]>([]);

    const [preview, setPreview] = useState<{
      type: string;
      url: string;
      nm_file: string;
    }>({ type: "none", url: "", nm_file: "" });

    const previewUrlRef = useRef<string>("");
    const previewSeqRef = useRef(0);

    const cdModule = globalMenu.getState().activeMenuInfo.cd_module || "";
    const noDocu = selectedData?.cd_group || "";

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_RUN_REG_001_CHEM_POP_004",
            {}
          );
          setColums(rtn.grid.grid1);

          setTimeout(() => {
            onSearch();
          }, 100);
        } catch (error) {}
      };
      fetchform();

      return () => {
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
      };
    }, []);

    const onSearch = async () => {
      try {
        if (!noDocu) {
          setDataSource([]);
          return;
        }

        const params = {
          id: "com_attachfile_01",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_menu: RECIPE_CD_MENU,
            no_docu: noDocu,
            no_line_docu: 0,
          },
        };
        const result: any = await fnGetDataSet(params);
        const rows = result[0] || [];
        setDataSource(rows);

        if (rows.length > 0) {
          setTimeout(() => {
            gridRef.current?.selectAndFocusRow(0);
          }, 100);
        } else {
          loadPreview(null);
        }
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    const loadPreview = async (row: any) => {
      const seq = ++previewSeqRef.current;

      const applyPreview = (next: {
        type: string;
        url: string;
        nm_file: string;
      }) => {
        if (seq !== previewSeqRef.current) {
          if (next.url) URL.revokeObjectURL(next.url);
          return;
        }
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = next.url;
        setPreview(next);
      };

      if (!row) {
        applyPreview({ type: "none", url: "", nm_file: "" });
        return;
      }

      const nmFile: string = row.nm_file || "";
      const ext = nmFile.split(".").pop()?.toLowerCase() || "";
      const isImage = IMAGE_EXTS.includes(ext);
      const isPdf = ext === "pdf";

      if (!isImage && !isPdf) {
        applyPreview({ type: "unsupported", url: "", nm_file: nmFile });
        return;
      }

      try {
        const res = await axios.post(
          "/snotes/file/download",
          { id: row.id, cdModule: cdModule },
          {
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
            responseType: "blob",
          }
        );

        const blob = isPdf
          ? new Blob([res.data], { type: "application/pdf" })
          : new Blob([res.data]);
        const url = URL.createObjectURL(blob);
        applyPreview({
          type: isPdf ? "pdf" : "image",
          url,
          nm_file: nmFile,
        });
      } catch (err) {
        console.error("미리보기 로드 실패:", err);
        applyPreview({ type: "error", url: "", nm_file: nmFile });
      }
    };

    const onFocusedRowChanged = (focusedRowData: any | null) => {
      loadPreview(focusedRowData);
    };

    const renderPreview = () => {
      switch (preview.type) {
        case "image":
          return (
            <img
              src={preview.url}
              alt={preview.nm_file}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          );
        case "pdf":
          return (
            <iframe
              src={preview.url}
              title={preview.nm_file}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          );
        case "unsupported":
          return (
            <p style={{ color: "gray" }}>
              미리보기를 지원하지 않는 파일 형식입니다. ({preview.nm_file})
            </p>
          );
        case "error":
          return (
            <p style={{ color: "gray" }}>미리보기를 불러오지 못했습니다.</p>
          );
        default:
          return <p style={{ color: "gray" }}>파일을 선택해주세요.</p>;
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "6px",
          }}
        >
          <SnButton text="조회" onClick={onSearch} />
        </div>
        <div className="w-full flex flex-1 min-h-0 space-x-1.5">
          <SnResizeBar direction={"width"} id="sopPanel1" orgSize="35%">
            <SnGrid
              ref={gridRef}
              columnConfig={columns}
              rowData={dataSource}
              etcProps={{
                rowSelection: "single",
                isEditable: false,
                suppressDragLeaveHidesColumns: true, // 26.09.07 kdy 태블릿에서 헤더 드래그로 컬럼 사라지는 것 방지
              }}
              events={{ onFocusedRowChanged }}
            />
          </SnResizeBar>
          <div
            className="flex-1 min-w-0 h-full"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #babfc7",
              overflow: "hidden",
            }}
          >
            {renderPreview()}
          </div>
        </div>
      </div>
    );
  }
);

export default SN_PRD_RUN_REG_001_CHEM_POP_004;
